'use client';

import { useMemo } from 'react';
import BreadcrumbNav from '@/components/seo/BreadcrumbNav';
import { useCmsData, useSiteSettings } from '@/lib/CmsContext';

/**
 * @param {import('@/types/cms').TeamMember[]} members
 * @param {import('@/types/cms').TeamSectionPageSection} section
 */
function pickMembers(members, section) {
  if (!Array.isArray(members) || !members.length) return [];

  const sorted = [...members].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const ids = Array.isArray(section.member_ids) ? section.member_ids.map(Number) : [];

  if (ids.length) {
    const byId = new Map(sorted.map((m) => [Number(m.wp_id || m.id), m]));
    return ids.map((id) => byId.get(id)).filter(Boolean);
  }

  return sorted;
}

/**
 * Team carousel — data from CPT team_member; navigation via ThemeScript.
 *
 * @param {{ section?: import('@/types/cms').TeamSectionPageSection, breadcrumbs?: Array<{ label: string, href: string }> | null }} props
 */
export default function TeamSection({ section = {}, breadcrumbs = null }) {
  const cms = useCmsData();
  const { phoneTel } = useSiteSettings();

  const team = useMemo(
    () =>
      pickMembers(cms?.team_member || [], section)
        .map((m) => ({
          type: m.media_type === 'video' ? 'video' : 'photo',
          src: m.media_url || '',
          name: m.name || '',
          nick: m.nick || '',
          role: m.role || '',
          sub: m.subtitle || '',
        }))
        .filter((m) => m.src || m.name),
    [cms?.team_member, section],
  );

  if (!team.length) return null;

  const eyebrow = section.eyebrow || 'Наша команда';
  const titleLine1 = section.title_line1 || 'Люди, які';
  const titleHighlight = section.title_highlight || 'будують твій дім';
  const showCta = section.show_cta !== false;
  const ctaTitle = (section.cta_title || '').trim() || 'Хочеш до';
  const ctaHighlight = (section.cta_highlight || '').trim() || 'команди?';
  const ctaText = (section.cta_text || '').trim() || 'Набираємо хлопців, які вміють і хочуть працювати.';
  const ctaButton = (section.cta_button_label || '').trim() || 'Доєднатись';

  const slides = showCta ? [...team, { type: 'cta' }] : team;
  const slideCount = slides.length;

  return (
    <section
      className="grv-team bg-dark pt-28 pb-16 overflow-hidden"
      data-grv-team
      data-grv-team-index="0"
      data-grv-team-count={String(slideCount)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-10">
        {breadcrumbs?.length ? (
          <div className="mb-5">
            <BreadcrumbNav items={breadcrumbs} />
          </div>
        ) : null}
        {eyebrow ? (
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-px bg-gold" />
            <span className="text-gold text-xs font-semibold uppercase tracking-widest">{eyebrow}</span>
          </div>
        ) : null}
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight">
          {titleLine1}
          {titleLine1 && titleHighlight ? <br /> : null}
          {titleHighlight ? <span className="text-gold-gradient">{titleHighlight}</span> : null}
        </h2>
      </div>

      <div className="relative px-4 sm:px-6 max-w-7xl mx-auto" data-grv-team-viewport>
        <div className="grv-team-track flex gap-4" data-grv-team-track>
          {slides.map((member, i) => {
            const isCta = member.type === 'cta';
            return (
              <div
                key={isCta ? 'cta' : `${member.name}-${i}`}
                data-grv-team-slide
                data-index={String(i)}
                className={`grv-team-slide shrink-0 rounded-2xl overflow-hidden border border-white/10 relative cursor-pointer${
                  i === 0 ? ' is-active' : ''
                }`}
                style={{
                  width: '66%',
                  aspectRatio: '9/14',
                  maxHeight: '75vh',
                }}
              >
                {isCta ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-dark-card gap-6 p-8 text-center">
                    <div className="gold-divider mx-auto" />
                    <h3 className="text-white text-2xl font-black leading-snug">
                      {ctaTitle}
                      <br />
                      <span className="text-gold-gradient">{ctaHighlight}</span>
                    </h3>
                    {ctaText ? (
                      <p className="text-white/50 text-sm leading-relaxed">{ctaText}</p>
                    ) : null}
                    {ctaButton && phoneTel ? (
                      <a
                        href={`tel:${phoneTel}`}
                        className="flex items-center gap-2 bg-gold-gradient text-dark font-bold px-8 py-3.5 rounded-lg hover:opacity-90 transition-opacity text-sm uppercase tracking-wider"
                      >
                        {ctaButton}
                      </a>
                    ) : null}
                  </div>
                ) : member.type === 'video' ? (
                  <div className="relative w-full h-full" data-grv-team-video-wrap>
                    <video
                      data-grv-team-video
                      src={member.src}
                      loop
                      playsInline
                      muted
                      preload="metadata"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      data-grv-team-video-play
                      className="grv-team-video-play absolute inset-0 z-10 flex items-center justify-center cursor-pointer"
                      aria-label="Відтворити відео"
                    >
                      <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gold/90 border border-gold flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-110">
                        <svg
                          viewBox="0 0 24 24"
                          fill="#141414"
                          className="w-6 h-6 sm:w-7 sm:h-7 ml-1 pointer-events-none"
                          aria-hidden="true"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </button>
                  </div>
                ) : (
                  <img
                    src={member.src}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                )}

                {!isCta ? (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 pointer-events-none">
                      {member.nick ? (
                        <p className="text-gold text-xs font-semibold uppercase tracking-widest mb-1">{member.nick}</p>
                      ) : null}
                      <h3 className="text-white text-xl font-black leading-tight">{member.name}</h3>
                      {member.role ? (
                        <p className="text-gold font-semibold text-sm mt-1">{member.role}</p>
                      ) : null}
                      {member.sub ? (
                        <p className="text-white/50 text-xs mt-0.5">{member.sub}</p>
                      ) : null}
                    </div>
                  </>
                ) : null}

                <div className="grv-team-dim absolute inset-0 bg-black/50 pointer-events-none" />
              </div>
            );
          })}
        </div>
      </div>

      {slideCount > 1 ? (
        <div className="flex justify-center gap-2 mt-8" data-grv-team-dots>
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              data-grv-team-dot
              data-index={String(i)}
              aria-label={`Слайд ${i + 1}`}
              className={`grv-team-dot rounded-full transition-all duration-300 ${
                i === 0 ? 'is-active w-6 h-2 bg-gold' : 'w-2 h-2 bg-white/30 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
